import { Alert, Box, Button, Card, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, Icon, InputLabel, MenuItem, Modal, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Close, Check, CheckCircleOutline, CheckCircle, Cancel, ErrorOutline } from "@mui/icons-material";
import { Fragment, useState } from "react";
import InvoicePreview from "./components/InvoicePreview";
import { convertToINR, format_mmmdyyyy } from "../../helpers/Utils";
import styled from "@emotion/styled";
import { getInvoiceRevisionHistory, setInvoiceFinanceDetails, updateInvoice } from "../../services/invoiceService";
import { useToast } from "./hooks/useToast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApprovalStatus } from "./constants/statusMapping";
import OrderStatusBadge from "./components/OrderStatusBadge";
import { COLOR_CONSTANTS } from "../../helpers/Colors";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import { getApprovalPayload, getRejectPayload } from "./utils/approveReject";
import { PERMISSIONS } from "./constants/permissions";
import { useApprovalMapping } from "./hooks/useApprovalMapping";
import { useInvoiceV2Flag } from "./hooks/useInvoiceV2Flag";
import { useIsMobile } from "../../hooks/useIsMobile";
import StickyActionBar from "../../components/mobile/StickyActionBar";

const productTypes = [{
    type: "product",
    label: "Product",
    value: 0
}, {
    type: "services",
    label: "Services",
    value: 1
}, {
    type: "installation",
    label: "Installation",
    value: 2
}, {
    type: "shipping",
    label: "Shipping",
    value: 3
}, {
    type: "loading",
    label: "Loading / Unloading",
    value: 4
}, {
    type: "floorCovering",
    label: "Floor Covering Materials",
    value: 5
}]

const CustomTextField = styled(TextField)({
    "& .MuiInputBase-input.Mui-disabled": {
        color: "rgba(153, 153, 153, 1)",
        borderColor: "rgba(230, 230, 230, 1)"
    }
});

const journalCodeList = [{
    type: 0,
    label: "VPIN"
}, {
    type: 1,
    label: "VPDN"
}, {
    type: 2,
    label: "GJV"
}]

const fd2RevCategoryList = [{
    type: 0,
    label: "PVTK"
}, {
    type: 1,
    label: "PVTF"
}, {
    type: 2,
    label: "RAW"
}, {
    type: 3,
    label: "BRDF"
}]

const tdsGroupList = [{
    type: "194Q",
    label: "194Q"
}, {
    type: "194CC",
    label: "194CC"
}, {
    type: "194CNC",
    label: "194CNC"
}]

function FinanceField({ id, label, compact, children }) {
    return (
        <Box display={"flex"} flexDirection={compact ? "column" : "row"} alignItems={compact ? "stretch" : "center"} gap={compact ? "4px" : "24px"}>
            <InputLabel
                htmlFor={id}
                sx={{ width: compact ? "auto" : "210px" }}
            >
                <Typography variant="subtitle1">{label}</Typography>
            </InputLabel>
            {children}
        </Box>
    );
}

export default function InvoiceReview({ invoice, handleClose }) {
    const [journalCode, setJournalCode] = useState(0);
    const [vendorACNumber, setVendorACNumber] = useState('');
    const [fd5City, setFd5City] = useState('');
    const [fd3Location, setFd3Location] = useState('');
    const [vendorAddressId, setVendorAddressId] = useState('');
    const [companyAddressId, setCompanyAddressId] = useState('');
    const [fd2RevCategory, setFd2RevCategory] = useState('');
    const [taxExempt, setTaxExempt] = useState('');
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const queryClient = useQueryClient();
    const isMobileViewport = useIsMobile();
    const { isV2 } = useInvoiceV2Flag();
    const isMobile = isV2 && isMobileViewport;
    const [productType, setProductType] = useState({
        product: {},
        services: {},
        installation: {},
        shipping: {},
        loading: {},
        floorCovering: {}
    })
    const childModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '92vw' : 560,
        bgcolor: 'background.paper',
        borderRadius: '4px'
    };
    const { showToast } = useToast();
    const [isClicked, setIsClicked] = useState('');
    const [showApprovalWarningModal, setShowApprovalWarningModal] = useState(false);

    // Maps the approval-level string from getApprovalPayload/getRejectPayload to the
    // user's Bouncer permission. Shared with InvoiceDetails.js (see useApprovalMapping).
    const { approvalMapping } = useApprovalMapping();

    // Resolve the current approval tier dynamically (vm -> l1 -> l2) instead of
    // hardcoding L2. Each button gates on its own payload (same as InvoiceDetails.js)
    // rather than a shared value, so the two flows can diverge safely in future.
    const approvalPayload = getApprovalPayload(invoice);
    const rejectPayload = getRejectPayload(invoice);
    const isL2 = approvalPayload.key === "l2";
    const canApprove = approvalMapping(approvalPayload.permission);
    const canReject = approvalMapping(rejectPayload.permission);

    // Tooltip explains why an action is blocked: when the tier is `deny` there is
    // nothing left to action (e.g. invoice completed) so surface the payload
    // message; otherwise it is a missing Bouncer permission.
    const disabledReason = (payload) =>
        payload.permission === PERMISSIONS.deny
            ? (payload.message || "No pending approval action for this invoice")
            : "You do not have permission to perform this approval action";

    const {
        isError,
        data: invoiceHistory,
    } = useQuery(
        ["invoiceHistory", invoice.id],
        async () => {
            const response = await getInvoiceRevisionHistory(invoice.id);
            return response ? formatRevisionHistory(response) : [];
        },
        {
            retry: false
        }
    );

    const formatRevisionHistory = (records) => {
        const history = [];
        let previousRecord;
        let sortedRecords = records.sort((a, b) => a.id - b.id);
        let keysToCompare = ["l1_approval_status", "l1_reject_reason", "l2_approval_status", "l2_reject_reason", "vm_approval_status", "vm_reject_reason"]
        for (const record of sortedRecords) {
            if (!previousRecord) {
                // First record, no changes to show
                previousRecord = record;
                continue;
            }

            const changes = {};

            // Iterate over fields and check for changes
            for (const key in record) {
                if (record[key] !== previousRecord[key] && keysToCompare.indexOf(key) > -1) {
                    changes[key] = {
                        previousValue: previousRecord[key],
                        newValue: record[key],
                    };
                }
            }

            if (Object.keys(changes).length > 0) {
                // Changes found, create a new history record
                history.push({
                    id: record.id,
                    created: format_mmmdyyyy(record.created),
                    changes,
                });
            }

            previousRecord = record;
        }

        return history;
    }

    const handleInvoiceRejection = async () => {
        const { permission, key, message } = getRejectPayload(invoice);
        if (permission === PERMISSIONS.deny || !key || !approvalMapping(permission)) {
            showToast({ type: "error", message: message || "You do not have permission to perform this approval action" });
            return;
        }
        setIsClicked('reject');
        const rejectionPayload = {
            invoice_no: invoice.invoice_no,
            [`${key}_approval_status`]: "rejected",
            [`${key}_reject_reason`]: rejectionReason,
            po_code: invoice.po_code
        };
        try {
            await updateInvoice({ payload: rejectionPayload });
            // Invalidate both list keys: "invoiceListQuery" (InvoiceList, where this
            // modal opens from) and "invoiceList" (InvoiceDetails) — defensive, matches
            // CreditNoteReview so the modal stays correct if reused from either page.
            queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
            queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
            showToast({ type: "success", message: "Invoice rejected successfully" });
            handleClose();
        } catch (err) {
            showToast({ type: "error", message: `Unable to reject : ${getBackendErrorMessage(err, "Something went wrong")}` });
        } finally {
            setIsClicked('');
        }
    }

    const handleChange = (event) => {
        setProductType({
            ...productType,
            [event.target.name]: {
                ["isChecked"]: event.target.checked
            },
        });
    };

    const handleTableInput = (type, key, value) => {
        setProductType({
            ...productType,
            [type]: {
                ...productType[type],
                [key]: value
            },
        });
    };

    const handleInvoiceApprove = async () => {
        const { permission, key, message } = getApprovalPayload(invoice);
        if (permission === PERMISSIONS.deny || !key || !approvalMapping(permission)) {
            showToast({ type: "error", message: message || "You do not have permission to perform this approval action" });
            return;
        }
        /** D365 fields are intentionally non-mandatory — form validation is disabled. */
        setIsClicked('approve');
        try {
            // D365 finance details are captured only at the final L2/finance approval.
            // VM/OPS and L1 approvals skip the finance form and finance-details call.
            if (key === "l2") {
                let invoiceProducts = []
                for (var i = 0; i < productTypes.length; i++) {
                    let type = productTypes[i].type;
                    if (productType[type].isChecked) {
                        invoiceProducts.push({
                            "product_type": productTypes[i].value,
                            "ledger_account_no": productType[type].ledger_account_no,
                            "amount": parseFloat(productType[type].amount),
                            "tds_group": productType[type].tds_group,
                            "gst_rate": parseFloat(productType[type].gst_rate),
                            "hsn_code": productType[type].hsn_code,
                            "sac_code": productType[type].sac_code
                        })
                    }
                }
                let financePayload = {
                    "invoice_no": invoice.invoice_no,
                    "po_code": invoice.po_code,
                    "journal_code": journalCode,
                    "fd5_city": fd5City,
                    "fd3_location": fd3Location,
                    "fd2_revenue_cat": parseFloat(fd2RevCategory),
                    "vendor_address_id": vendorAddressId,
                    "company_address_id": companyAddressId,
                    "vendor_account_no": vendorACNumber,
                    "tax_exempt": taxExempt === "true",
                    "invoice_products": invoiceProducts
                }
                await setInvoiceFinanceDetails(financePayload);
            }
            const approvePayload = {
                invoice_no: invoice.invoice_no,
                [`${key}_approval_status`]: "approved",
                po_code: invoice.po_code
            };
            await updateInvoice({ payload: approvePayload });
            queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
            queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
            showToast({ type: "success", message: "Invoice approved successfully" });
            handleClose();
        } catch (err) {
            showToast({ type: "error", message: `Unable to approve : ${getBackendErrorMessage(err, "Something went wrong")}` });
        } finally {
            setIsClicked('');
        }
    }

    const { parser_invoice_po_result } = invoice.validation_result || {}
    const { validation_field_results } = parser_invoice_po_result || {};

    const totalValidatableFields = Object.keys(validation_field_results || {}).length;
    const unMatchedFields = Object.keys(validation_field_results || {}).filter((field) => !validation_field_results?.[field]?.match_status);

    // ENG-19487: FINS POs are first-touch approved by OPS, so the revision-history
    // chip reads "OPS …" instead of "VM …". This is a label-only change — the
    // underlying vm_approval_status field and vm_approval permission are unchanged.
    // `po_source` is the JSON key VIP emits for the originating system
    // (FINS/IMS/ZOHO); see VIP InvoiceListModel / InvoiceDetailModel.
    const firstTouchApproverLabel = invoice?.po_source === "FINS" ? "OPS" : "VM";

    const validateNode = (field) => {
        if(invoice.system_validation_status == null) return "IGNORE";
        if (validation_field_results && validation_field_results.hasOwnProperty(field)) {
            return validation_field_results[field].match_status;
        }
        return "IGNORE"
    }

    const actionButtons = (
        <>
            <Tooltip title={canReject ? "" : disabledReason(rejectPayload)} disableHoverListener={canReject}>
                <span>
                    <Button color="secondary" variant="contained" fullWidth={isMobile} startIcon={<Close />} disabled={!canReject || !!isClicked} onClick={() => setShowRejectionModal(true)}>Reject</Button>
                </span>
            </Tooltip>
            <Tooltip title={canApprove ? "" : disabledReason(approvalPayload)} disableHoverListener={canApprove}>
                <span>
                    <Button variant="contained" fullWidth={isMobile} startIcon={<Check />} onClick={() => {
                        if (unMatchedFields?.length > 0) {
                            setShowApprovalWarningModal(true);
                        } else {
                            handleInvoiceApprove();
                        }
                    }} disabled={!canApprove || !!isClicked} endIcon={isClicked === 'approve' ? <CircularProgress size={14} thickness={6} /> : ''}>Approve</Button>
                </span>
            </Tooltip>
        </>
    );

    return (
        <Modal open={!!invoice} onClose={handleClose}>
            <Box height={"100vh"} display={"flex"} flexDirection={"column"}>
                <Box sx={{ height: '56px', padding: '10px 8px', bgcolor: 'white', display: 'flex', flexDirection: 'row', alignItems: 'center', borderBottom: '1px solid #ccc' }}>
                    <Icon onClick={handleClose} style={{ cursor: 'pointer' }}>
                        <Close fontSize="24px" />
                    </Icon>
                    <Box paddingLeft={"12px"}>
                        <Typography variant="body1">Invoice {invoice.invoice_no}</Typography>
                        <Typography variant="subtitle1" style={{ color: 'black' }}>Review Invoice</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1, bgcolor: "#F2F2F2", overflowY: "scroll" }}>
                    {invoice.system_validation_status != null && unMatchedFields?.length ? <Box bgcolor={COLOR_CONSTANTS["tertiary-red/variant2"]} display={'flex'} alignItems={'center'} gap={1} padding={1} my={2} mx={isMobile ? 1.5 : 3}>
                        <ErrorOutline style={{ color: COLOR_CONSTANTS["tertiary-red/default"] }} />
                        <p className="ft-14 bold" style={{ color: COLOR_CONSTANTS["tertiary-red/default"] }}>
                            {`${unMatchedFields?.length}/${totalValidatableFields} fields require manual review`}
                        </p>
                    </Box> : null}
                    <Grid container spacing={isMobile ? '12px' : '24px'} padding={isMobile ? '12px' : '24px'} borderBottom={'1px solid #CCCCCC'}>
                        <Grid item xs={isMobile ? 12 : 8}>
                            <Box>
                                <Typography variant="h4" marginBottom={"14px"}>Uploaded invoice</Typography>
                                {invoice?.invoice_image?.pre_signed_url ? (
                                    <InvoicePreview type={invoice.invoice_image.file_name?.split('.').pop()} url={invoice.invoice_image.pre_signed_url} scale={isMobile ? 1 : 1.5} />
                                ) : (
                                    <Typography color="textSecondary">No invoice document available</Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 4}>
                            <Box>
                                <Typography variant="h4" marginBottom={"14px"}>Invoice validation checks</Typography>
                                <Card sx={{ padding: "14px" }}>
                                    <Grid container spacing={isMobile ? "12px" : "24px"}>
                                        {[
                                            {
                                                label: "PO Number",
                                                value: invoice.po_code,
                                                validation: validateNode('po_code')
                                            },
                                            {
                                                label: "PO Value",
                                                value: convertToINR(invoice.po_value),
                                                validation: validateNode('invoice_value'),
                                            },
                                            {
                                                label: "Billing Entity",
                                                value: invoice.billing_entity || "-",
                                                validation: validateNode("bill_to_name"),
                                            },
                                            {
                                                label: "Bill to Address",
                                                value: invoice.bill_to_address || "-",
                                                validation: validateNode('bill_to_address'),
                                            },
                                            {
                                                label: "Ship to Address",
                                                value: invoice.ship_to_address || "-",
                                                validation: validateNode('ship_to_address'),
                                            },
                                            {
                                                label: "HSN / SAC",
                                                value: invoice.hsn_sac_codes?.join(", ") || "-",
                                                validation: validateNode('hsn_code'),
                                            },
                                            {
                                                label: "GRN ID",
                                                value: invoice.grn_id || "-",
                                                validation: "IGNORE"
                                            },
                                            {
                                                label: "GRN Value",
                                                value: convertToINR(invoice.grn_value) || "-",
                                                validation: "IGNORE"
                                            },
                                            {
                                                label: "PID",
                                                value: invoice.project_id || "-",
                                                validation: "IGNORE"
                                            },
                                            {
                                                label: "Order ID",
                                                value: invoice.order_id || "-",
                                                validation: "IGNORE"
                                            },
                                            {
                                                label: "PO Status",
                                                value: invoice.po_status,
                                                validation: "IGNORE"
                                            },
                                        ].map((item, index) => (
                                            <Fragment key={index}>
                                                <Grid item xs={6}>
                                                    <Box display={"flex"} gap={2} alignItems={"center"}>
                                                        {item.validation !== 'IGNORE' ? <>
                                                            {item.validation ? <CheckCircle
                                                                style={{ color: COLOR_CONSTANTS["tertiary-green/variant"] }}
                                                            />
                                                                : <Cancel style={{ color: COLOR_CONSTANTS["tertiary-red/default"] }} />
                                                            }
                                                        </> : <Box height={24} width={24} />}
                                                        <Typography variant="subtitle1">
                                                            {item.label}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="body1">
                                                        {item.value || "-"}
                                                    </Typography>
                                                </Grid>
                                            </Fragment>
                                        ))}
                                    </Grid>
                                </Card>
                            </Box>
                            <Box marginTop={"24px"}>
                                <Typography variant="h4" marginBottom={"14px"}>Revision history</Typography>
                                <Card sx={{ padding: "14px" }}>
                                    <Box display={'flex'} gap={"8px"} flexDirection={"column"} maxHeight={200} overflow={'scroll'}>
                                        {!isError && invoiceHistory?.map((item, index) => <Grid key={index} container spacing={"24px"}>
                                            <Grid item xs={4}>
                                                <Typography variant="subtitle1">{item.created}</Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                {item.changes.vm_approval_status && <OrderStatusBadge
                                                    type={
                                                        ApprovalStatus[item.changes.vm_approval_status.newValue]?.code
                                                    }
                                                    label={firstTouchApproverLabel + " " + ApprovalStatus[item.changes.vm_approval_status.newValue]?.label}
                                                />}
                                                {item.changes.l1_approval_status && <OrderStatusBadge
                                                    type={
                                                        ApprovalStatus[item.changes.l1_approval_status.newValue]?.code
                                                    }
                                                    label={"L1 " + ApprovalStatus[item.changes.l1_approval_status.newValue]?.label}
                                                />}
                                                {item.changes.l2_approval_status && <OrderStatusBadge
                                                    type={
                                                        ApprovalStatus[item.changes.l2_approval_status.newValue]?.code
                                                    }
                                                    label={"L2 " + ApprovalStatus[item.changes.l2_approval_status.newValue]?.label}
                                                />}
                                                {(item.changes.l1_reject_reason?.newValue || item.changes.l2_reject_reason?.newValue || item.changes.vm_reject_reason?.newValue) && <Typography variant="body1" marginTop={"4px"}>Rejection reason - {item.changes.l1_reject_reason?.newValue || item.changes.l2_reject_reason?.newValue || item.changes.vm_reject_reason?.newValue}</Typography>}
                                            </Grid>
                                        </Grid>)}
                                        {invoiceHistory?.length === 0 && <Alert severity="info">No records</Alert>}
                                        {isError && <Alert severity="error">Failed to fetch Revision History</Alert>}
                                    </Box>
                                </Card>
                            </Box>
                        </Grid>
                    </Grid>
                    {isL2 && <Box padding={isMobile ? '12px' : '24px'}>
                        <Typography variant="h4" marginBottom={"14px"}>Required fields for approval</Typography>
                        <Card sx={{ padding: "14px" }}>
                            <Box display={"flex"} flexDirection={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} marginBottom={"16px"}>
                                <Typography width={isMobile ? "auto" : "180px"} variant="subtitle1">Product type</Typography>
                                <Box>
                                    <FormGroup row>
                                        <FormControlLabel control={<Checkbox checked={productType.product.isChecked} name="product" onChange={handleChange} />} label="Product" />
                                        <FormControlLabel control={<Checkbox checked={productType.services.isChecked} name="services" onChange={handleChange} />} label="Service" />
                                        <FormControlLabel control={<Checkbox checked={productType.installation.isChecked} name="installation" onChange={handleChange} />} label="Installation" />
                                        <FormControlLabel control={<Checkbox checked={productType.shipping.isChecked} name="shipping" onChange={handleChange} />} label="Shipping" />
                                        <FormControlLabel control={<Checkbox checked={productType.loading.isChecked} name="loading" onChange={handleChange} />} label="Loading / Unloading" />
                                        <FormControlLabel control={<Checkbox checked={productType.floorCovering.isChecked} name="floorCovering" onChange={handleChange} />} label="Floor Covering materials" />
                                    </FormGroup>
                                </Box>
                            </Box>
                            <Grid container xs={isMobile ? 12 : 10} columnSpacing={isMobile ? 0 : "120px"} rowSpacing={"16px"}>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"journalCode"} label="Journal code" compact={isMobile}>
                                        <TextField
                                            select
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            id="journalCode"
                                            value={journalCode}
                                            onChange={(e) => setJournalCode(e.target.value)}
                                            fullWidth
                                        >
                                            {journalCodeList.map((item) => <MenuItem key={item.type} value={item.type}>{item.label}</MenuItem>)}
                                        </TextField>
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"vendorAcNo"} label="Vendor account no." compact={isMobile}>
                                        <TextField
                                            id="vendorAcNo"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            value={vendorACNumber}
                                            onChange={(e) => setVendorACNumber(e.target.value)}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Enter value"
                                        />
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"fd5City"} label="FD5 City" compact={isMobile}>
                                        <TextField
                                            id="fd5City"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            value={fd5City}
                                            onChange={(e) => setFd5City(e.target.value)}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Enter value"
                                        />
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"vendorAddrId"} label="Vendor address ID" compact={isMobile}>
                                        <TextField
                                            id="vendorAddrId"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            variant="outlined"
                                            fullWidth
                                            value={vendorAddressId}
                                            onChange={(e) => setVendorAddressId(e.target.value)}
                                            placeholder="Enter value"
                                        />
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"fd3Location"} label="FD3 Location" compact={isMobile}>
                                        <TextField
                                            id="fd3Location"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            value={fd3Location}
                                            onChange={(e) => setFd3Location(e.target.value)}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Enter value"
                                        />
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"companyAddrId"} label="Company address ID" compact={isMobile}>
                                        <TextField
                                            id="companyAddrId"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            variant="outlined"
                                            fullWidth
                                            value={companyAddressId}
                                            onChange={(e) => setCompanyAddressId(e.target.value)}
                                            placeholder="Enter value"
                                        />
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"companyAddrId"} label="FD2 Revenue category" compact={isMobile}>
                                        <TextField
                                            id="fd2RevCategory"
                                            select
                                            value={fd2RevCategory}
                                            onChange={(e) => setFd2RevCategory(e.target.value)}
                                            fullWidth
                                        >
                                            {fd2RevCategoryList.map((item) => <MenuItem key={item.type} value={item.type}>{item.label}</MenuItem>)}
                                        </TextField>
                                    </FinanceField>
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6}>
                                    <FinanceField id={"taxExempt"} label="Tax Exempt" compact={isMobile}>
                                        <Box>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="taxExempt"
                                                    name="radio-buttons-group"
                                                    id="taxExempt"
                                                    row
                                                    value={taxExempt}
                                                    onChange={(e) => setTaxExempt(e.target.value)}
                                                >
                                                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                                    <FormControlLabel value="false" control={<Radio />} label="No" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                    </FinanceField>
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>}
                    {isL2 && <Box padding={isMobile ? '12px' : '24px'}>
                        <Typography variant="h4" marginBottom={"14px"}>Product type specific fields</Typography>
                        <TableContainer component={Card}>
                            <Table sx={{ minWidth: isMobile ? 900 : undefined }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product type</TableCell>
                                        <TableCell>Ledger number</TableCell>
                                        <TableCell>Credit amount</TableCell>
                                        <TableCell>TDS Group</TableCell>
                                        <TableCell>GST Rate Type</TableCell>
                                        <TableCell>HSN Code</TableCell>
                                        <TableCell>SAC Code</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productTypes.map((row) => (
                                        <TableRow
                                            key={row.label}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {row.label}
                                            </TableCell>
                                            <TableCell>
                                                <CustomTextField onChange={(e) => handleTableInput(row.type, 'ledger_account_no', e.target.value)} value={productType[row.type].ledger_account_no} placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small" />
                                            </TableCell>
                                            <TableCell><CustomTextField onChange={(e) => handleTableInput(row.type, 'amount', e.target.value)} value={productType[row.type].amount} type="number" placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small" /></TableCell>
                                            <TableCell><CustomTextField select fullWidth onChange={(e) => handleTableInput(row.type, 'tds_group', e.target.value)} value={productType[row.type].tds_group} type="number" placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small">
                                                {tdsGroupList.map((item) => <MenuItem key={item.type} value={item.type}>{item.label}</MenuItem>)}
                                            </CustomTextField></TableCell>
                                            <TableCell><CustomTextField onChange={(e) => handleTableInput(row.type, 'gst_rate', e.target.value)} value={productType[row.type].gst_rate} type="number" placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small" /></TableCell>
                                            <TableCell><CustomTextField onChange={(e) => handleTableInput(row.type, 'hsn_code', e.target.value)} value={productType[row.type].hsn_code} type="number" placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small" /></TableCell>
                                            <TableCell><CustomTextField onChange={(e) => handleTableInput(row.type, 'sac_code', e.target.value)} value={productType[row.type].sac_code} type="number" placeholder="Enter value" disabled={!productType[row.type].isChecked} size="small" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>}
                </Box>
                {isMobile ? (
                    <StickyActionBar>{actionButtons}</StickyActionBar>
                ) : (
                    <Box boxShadow={"0px 4px 4px 4px rgba(0, 0, 0, 0.25)"} display={"flex"} justifyContent={"flex-end"} alignItems={"center"} columnGap={"12px"} sx={{ bgcolor: 'white', padding: '24px' }}>
                        {actionButtons}
                    </Box>
                )}
                <Modal open={showRejectionModal} onClose={() => setShowRejectionModal(false)}>
                    <Box role="dialog" sx={childModalStyle}>
                        <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} borderBottom={"1px solid #E6E6E6"} padding={"16px"}>
                            <Typography variant="h4">Invoice Rejected</Typography>
                            <Icon sx={{ cursor: 'pointer' }} onClick={() => setShowRejectionModal(false)}>
                                <Close fontSize="24px" />
                            </Icon>
                        </Box>
                        <Box padding={"24px"}>
                            <InputLabel htmlFor="rejectionReasons">Enter rejection reason</InputLabel>
                            <TextField
                                id="rejectionReasons"
                                InputLabelProps={{
                                    shrink: false
                                }}
                                sx={{ marginTop: "12px" }}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box display={"flex"} justifyContent={"flex-end"} alignItems={"center"} columnGap={"12px"} sx={{ padding: '24px' }}>
                            <Button color="secondary" variant="contained" onClick={() => setShowRejectionModal(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleInvoiceRejection} disabled={rejectionReason === '' || isClicked === 'reject'} endIcon={isClicked === 'reject' ? <CircularProgress size={14} thickness={6} /> : ''}>Reject</Button>
                        </Box>
                    </Box>
                </Modal>

                <Modal open={showApprovalWarningModal} onClose={() => setShowApprovalWarningModal(false)}>
                    <Box role="dialog" sx={childModalStyle}>
                        <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} borderBottom={"1px solid #E6E6E6"} padding={"16px"}>
                            <span className="ft-20 bold">Confirm Approval</span>
                            <Icon sx={{ cursor: 'pointer' }} onClick={() => setShowApprovalWarningModal(false)}>
                                <Close fontSize="24px" />
                            </Icon>
                        </Box>
                        <Box padding={"24px"}>
                            <span className="ft-16"> Are you sure you want to approve all the non-matching values?</span>
                        </Box>
                        <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} borderBottom={"1px solid #E6E6E6"}></Box>
                        <Box display={"flex"} justifyContent={"flex-end"} alignItems={"center"} columnGap={"12px"} sx={{ padding: '16px' }}>
                            <Button style={{ backgroundColor: COLOR_CONSTANTS["on-surface/secondary"] }} variant="contained" onClick={() => setShowApprovalWarningModal(false)}>Cancel</Button>
                            <Button variant="contained" onClick={() => {
                                setShowApprovalWarningModal(false);
                                handleInvoiceApprove();
                            }}>Approve</Button>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </Modal>
    )
}